// The frame blocks cruise owns. Built apart from `createCruiseSim` so the
// composed order can be read in a unit test without a renderer or a page.
import type * as THREE from 'three';
import type { FrameCtx, FrameSystem } from '../frame';
import { flightSubsteps, type FlightAxes, type FlightConfig, type FlightState, type FlightVec } from '../sim/flight';
import type { GravityField } from '../sim/gravityField';
import { spinAheadDir } from '../sim/ground';
import type { LandVerbHandle } from '../sim/landVerb';
import type { SimPositionHandle } from '../sim/position';
import type { TrajectoryHandle } from '../sim/trajectory';
import type { WorldFrameHandle, WorldVec } from '../world/frame';
import { bodyById } from '../world/bodies';
import { gameClock } from '../world/clock';
import type { SubtreeHandle, WorldSceneHandle } from '../world/scene';
import { activeStage } from '../world/stage';
import type { InputHandle } from '../input';
import type { HudButtonsHandle } from '../hudButtons';
import type { DvMapPanel } from '../hudDvMap';
import type { PilotPanel } from '../pilotPanel';
import { profiler } from '../perf';
import type { BodyLabelsHandle } from '../scene/bodyLabels';
import type { PostFxHandle } from '../scene/postFx';
import type { ProgradeHudHandle } from '../scene/progradeHud';
import type { DvCorrection } from '../sim/dvCorrection';
import type { DvMap } from '../sim/dvMap';
import type { AtlasWorldHandle } from '../sim/landVerb';
import { WARP_APPROACH_FACTOR, type WarpEnvelope, type WarpHandle } from '../sim/warp';
import type { CameraRig } from './cameraRig';
import type { CruiseDrive, CruiseDriveLive } from './drive';
import type { PilotTarget } from './pilotTarget';
import { TEST_SWITCHES } from '../urlFlags.ts';

export interface CruiseSystemsDeps {
  /** A fight flies its own movement model on the fixed step. */
  inFight(): boolean;
  /** The game's own clocks that tick ahead of the drive; play's HET timers. */
  beforeFly(dt: number): void;
  fly(realDt: number, dt: number, t: number): void;
}

export interface CruiseSystems {
  /** The live frame's cruise blocks; play splices its own in around `flight`. */
  live: FrameSystem[];
}

export function createCruiseSystems(deps: CruiseSystemsDeps): CruiseSystems {
  const whenLive = (c: FrameCtx): boolean => c.live;
  return {
    live: [
      { name: 'flight', when: whenLive, update: (c) => {
        // A fight's movement model is stepped above; what is left here is cruise.
        if (deps.inFight()) return;
        deps.beforeFly(c.dt);
        deps.fly(c.realDt, c.dt, c.t);
      } },
    ],
  };
}

// `?fightcarry=1`: a menu opened over a fight carries and coasts the ship as
// cruise does, as it did, leaving the fight it came from kilometres behind.
// `atlas-clock-check.mjs --inject fight-carry` reaches it.
// The `typeof location` guards let the order test import this module with no page.
const FIGHT_CARRY = typeof location !== 'undefined' && TEST_SWITCHES && new URLSearchParams(location.search).get('fightcarry') === '1';
// `?landdriftold=1` puts back the pre-fix landed write: the parent's own
// per-tick step summed into `simPos.world` instead of re-derived from the
// LAND pin, so a long stay drifts again. The broken-on-purpose variant
// `--ship --inject land-drift-old` opens it; no player reaches it.
const LAND_DRIFT_OLD = typeof location !== 'undefined' && TEST_SWITCHES && new URLSearchParams(location.search).get('landdriftold') === '1';
// `?landpininertial=1` holds the LAND ladder's pin fixed in the inertial
// frame instead of turning it with the body under it, which is the pin
// before this fix: the broken-on-purpose variant
// `grounded-view-check.mjs --inject pin-inertial` opens. Nothing a player
// reaches sets it.
const LAND_PIN_INERTIAL = typeof location !== 'undefined' && TEST_SWITCHES && new URLSearchParams(location.search).get('landpininertial') === '1';
// `?landpinspinorbit=1` turns the pin for a ship left in orbit too, as it did:
// `atlas-clock-check.mjs --leg surface --inject pin-spin-orbit` opens it.
const LAND_PIN_SPIN_ORBIT = typeof location !== 'undefined' && TEST_SWITCHES && new URLSearchParams(location.search).get('landpinspinorbit') === '1';
// `?worldlookhard=1` swaps the orrery's globe for the body's own look in one
// frame at the SYSTEM edge, as it did: `approach-look-check.mjs --inject
// hard-swap` opens it. Nothing a player reaches sets it.
const WORLD_LOOK_HARD = typeof location !== 'undefined' && TEST_SWITCHES && new URLSearchParams(location.search).get('worldlookhard') === '1';
/**
 * The body cruise draws with its own air, and how much of that look: 0 at
 * `WARP_APPROACH_FACTOR` envelopes of altitude (the reach inside which a closing
 * ship's warp is held to 1 HR/S, so the ease spans a hundred frames), 1 at the
 * SYSTEM edge and inside it. The orrery's globe and the body's own differ in exposure, limb and
 * shell, and one frame between the two read as the planet switching.
 */
export function worldLook(env: WarpEnvelope | null): { id: string | null; look: number } {
  if (env === null) return { id: null, look: 0 };
  if (WORLD_LOOK_HARD) return env.inside ? { id: env.id, look: 1 } : { id: null, look: 0 };
  const reachM = env.envelopeM * WARP_APPROACH_FACTOR;
  if (!(env.altitudeM < reachM)) return { id: null, look: 0 };
  const t = Math.min(1, Math.max(0, (reachM - env.altitudeM) / (reachM - env.envelopeM)));
  return { id: env.id, look: t * t * (3 - 2 * t) };
}
/** No hand on the controls: what the menu branch's coast is flown with. */
const COAST_AXES: FlightAxes = { pitch: 0, yaw: 0, roll: 0 };

/**
 * Paused out of a fight. A fight holds its ship in the bubble's own frame, live
 * or behind a menu: the bubble rides its parent, and nothing carries the ship
 * out of it or flies its orbit.
 */
export function pausedCruise(c: FrameCtx, inFight: boolean): boolean {
  return c.paused && (!inFight || FIGHT_CARRY);
}

export interface CruiseMenuSystemsDeps {
  inFight(): boolean;
  clock: THREE.Clock;
  flight: FlightState;
  flightConfig: FlightConfig;
  simPos: SimPositionHandle;
  gravityField: GravityField;
  trajectory: TrajectoryHandle;
  landVerb: LandVerbHandle;
  live: CruiseDriveLive;
  rebaseFlightFrame(): void;
  advanceSimBodies(): void;
  frameParentStep(): FlightVec;
  stepFlightFrame(
    axes: FlightAxes, throttle: number, dt: number, attitudeDt: number,
    substeps: number, out: FlightVec, assistDt: number,
  ): void;
  noteGroundContact(): void;
  shipWorldAt(): WorldVec;
  sampleTrajectoryBody(i: number, aheadS: number, out: WorldVec, vel: WorldVec): void;
  syncTrajectoryCaption(): void;
  _step: THREE.Vector3;
  FLIGHT_CARRY_OLD_PARENT: boolean;
  BODIES_PINNED: boolean;
  MARKER_STALE: boolean;
  LINE_STALE: boolean;
}

/** The paused frame: a menu, the key list, or the land verb. Play splices `menu-fight-frame` in after `menu-bodies`. */
export function createCruiseMenuSystems(deps: CruiseMenuSystemsDeps): FrameSystem[] {
  const {
    inFight, clock, flight, flightConfig, simPos, gravityField, trajectory, landVerb, live,
    rebaseFlightFrame, advanceSimBodies, frameParentStep, stepFlightFrame, noteGroundContact,
    shipWorldAt, sampleTrajectoryBody, syncTrajectoryCaption, _step,
    FLIGHT_CARRY_OLD_PARENT, BODIES_PINNED, MARKER_STALE, LINE_STALE,
  } = deps;
  const {
    field: _field, ground: _ground, pullIds: _pullIds, pullRadiiM: _pullRadii,
    pullPeriodS: _pullPeriodS, pullParentIdx: _pullParentIdx,
    update: updateFlightField, refill: refillFlightField,
  } = gravityField;
  /** `menu-parent-carry`'s own scratch: the LAND pin turned forward to the
   *  current epoch, so the holding tick allocates nothing. */
  const _landPinSpun: FlightVec = { x: 0, y: 0, z: 0 };

  const whenPaused = (c: FrameCtx): boolean => c.paused;
  const whenPausedCruise = (c: FrameCtx): boolean => pausedCruise(c, inFight());
  /** Paused with the ship still flying: the land verb parks it instead. */
  const whenAfloat = (c: FrameCtx): boolean => whenPausedCruise(c) && !c.landOpen;

  return [

{ name: 'menu-flight-field', when: whenPausedCruise, update: () => {
  // The pick FIRST, then the carry on the parent that pick names: the same
  // order and for the same reason as the cruise branch below.
  if (!FLIGHT_CARRY_OLD_PARENT) {
    updateFlightField();
    rebaseFlightFrame();
  }
} },

{ name: 'menu-bodies', when: whenPausedCruise, update: () => {
  advanceSimBodies();
} },

{ name: 'menu-parent-carry', when: whenPausedCruise, update: () => {
  // Landed: the offset from the parent is meant to stay exactly fixed, so
  // it is re-derived from the LAND pin rather than integrated -
  // `frameParentStep()` differences two ~1e11 m doubles, and summing that
  // noise into `simPos.world` every tick is what let a long stay drift.
  const landPin = landVerb.parentOffset;
  const landPinId = landVerb.parentId;
  const landPinJd = landVerb.parentOffsetJd;
  const landedParentAt = landVerb.open && !LAND_DRIFT_OLD && landPin && landPinId !== null
    ? live.simBodies?.worldAt(landPinId) ?? null
    : null;
  if (landedParentAt && landPin) {
    // The pin itself is held fixed against the body's centre at the instant
    // LAND opened; turned forward here by the same spin the seat turns with,
    // so a long stay does not resume off the patch it landed on.
    _landPinSpun.x = landPin.x;
    _landPinSpun.y = landPin.y;
    _landPinSpun.z = landPin.z;
    const row = landPinId !== null ? bodyById(live.bodyRows, landPinId) : undefined;
    // Only a seated ship turns with the ground: an orbiting ship's held velocity
    // is not turned, so a turned pin hands it back falling after a warped stay.
    if (!LAND_PIN_INERTIAL && (flight.grounded === landPinId || LAND_PIN_SPIN_ORBIT) && row && landPinJd !== null && Number.isFinite(live.seedJd)) {
      spinAheadDir(row, live.seedJd, landPin.x, landPin.y, landPin.z,
        -(live.seedJd - landPinJd) * 86400, _landPinSpun);
    }
    simPos.holdAt({
      x: landedParentAt.x + _landPinSpun.x,
      y: landedParentAt.y + _landPinSpun.y,
      z: landedParentAt.z + _landPinSpun.z,
    });
    // The flight integrator is held while LAND is open, so `flight.groundOffset`
    // never sees this turn on its own: without this, the resumed contact in
    // `restOnGround` (`src/sim/flight.ts`) re-derives the seat from the stale
    // pre-LAND offset the instant the player returns, snapping the hull back
    // off the very patch the pin above just held it over.
    if (flight.grounded === landPinId) {
      flight.groundOffset.x = _landPinSpun.x;
      flight.groundOffset.y = _landPinSpun.y;
      flight.groundOffset.z = _landPinSpun.z;
    }
  } else {
    const parked = frameParentStep();
    simPos.advance(parked.x, parked.y, parked.z);
  }
} },

{ name: 'menu-field-refill', when: whenPausedCruise, update: () => {
  if (FLIGHT_CARRY_OLD_PARENT) {
    updateFlightField();
    rebaseFlightFrame();
  } else {
    refillFlightField();
  }
} },

{ name: 'menu-coast', when: whenAfloat, update: (c) => {
  const menuDt = c.dt;
  // The ship coasts while the map has the screen. The clock runs in this
  // branch, so the orbit has to run with it: a ship that only rides its
  // parent keeps its offset from that parent frame after frame, which draws a
  // marker that does not move against the body it is orbiting and jumps to
  // where the orbit really is the moment the view is closed. The player's own
  // controls are not live here - no axes, no throttle, no assist - and the
  // prediction is handed exactly that. The land verb keeps the old rule: the
  // Atlas has taken the camera to the ground and `returnToShip` puts the
  // pilot back at a ship that has not moved.
  if (!MARKER_STALE) {
    live.flightLastSubsteps = flightSubsteps(flightConfig, menuDt, _field.pulls.length);
    live.flightLastSubstepS = live.flightLastSubsteps > 0 ? menuDt / live.flightLastSubsteps : 0;
    _ground.contact.hit = false;
    stepFlightFrame(COAST_AXES, 0, menuDt, 0, live.flightLastSubsteps, _step, 0);
    noteGroundContact();
    refillFlightField();
    live.flightSimSeconds += menuDt;
    simPos.advance(_step.x, _step.y, _step.z);
  }
} },

{ name: 'menu-trajectory', when: whenAfloat, update: () => {
  // After the advance, so the near end leaves the ship where it now is, and
  // in this branch as well as the cruise one: a prediction that stops
  // rebuilding while the map is open stays anchored where the ship was when
  // it opened, and the bodies walk out from under it.
  if (!LINE_STALE) trajectory.update({
    nowMs: clock.elapsedTime * 1000,
    state: flight,
    at: shipWorldAt(),
    pulls: _field.pulls,
    pullIds: _pullIds,
    pullRadiiM: _pullRadii,
    surfaceAt: _ground.surfaceAt,
    pullOuterRadiiM: _ground.outerRadiiM,
    pullPeriodS: _pullPeriodS,
    pullParentIdx: _pullParentIdx,
    framePullIdx: gravityField.frameParentIdx,
    sampleAt: BODIES_PINNED ? null : sampleTrajectoryBody,
    axes: COAST_AXES,
    throttle: 0,
    rate: gameClock().rate,
    assist: false,
    simSeconds: live.flightSimSeconds,
  });
} },

{ name: 'menu-trajectory-caption', when: whenPaused, update: () => {
  // The block below that would otherwise keep this in sync is skipped for
  // this whole branch, and the Atlas's own stage still draws the trajectory
  // as one of its children.
  syncTrajectoryCaption();
} },

{ name: 'menu-stage', when: whenPaused, update: () => {
  // A stage mounted in the menu pane (the Atlas) draws into its own scissored
  // rect of this canvas instead. Render-on-demand lives inside that frame, so
  // an idle Atlas costs nothing here.
  activeStage()?.frame();
} },

  ];
}

/** The frame's seams the game fills; explore leaves each at its no-op. */
export interface CruiseViewSystemsDeps {
  inFight(): boolean;
  /** A fight's own camera. True when it placed the camera, so the chase rig stands down. */
  fightCamera(realDt: number): boolean;
  /** The ship's place re-derived after a rebase, in a fight, off the fixed step's alpha. */
  rebaseInFight(): void;
  /** Either side of `progradeHud.update()`. */
  sceneMisc: { before(c: FrameCtx): void; after(c: FrameCtx): void };
  /** Before the pilot panel, and between the HUD buttons and the delta-v map. */
  hud: { before(c: FrameCtx): void; after(c: FrameCtx): void };
  input: Pick<InputHandle, 'takeOrbit' | 'takeWheel'>;
  rig: Pick<CameraRig, 'update'>;
  bodyLabels: Pick<BodyLabelsHandle, 'update'>;
  camera: THREE.PerspectiveCamera;
  simPos: SimPositionHandle;
  syncShipTransform(): void;
  progradeHud: Pick<ProgradeHudHandle, 'update'>;
  worldFrame: WorldFrameHandle;
  atlasWorld(): AtlasWorldHandle | null;
  warp: Pick<WarpHandle, 'envelope'>;
  syncTrajectoryCaption(): void;
  simWorld: SubtreeHandle;
  world: WorldSceneHandle;
  postFx: PostFxHandle;
  pilotPanel: PilotPanel;
  pilot: Pick<PilotTarget, 'pilotReadout'>;
  trajectory: TrajectoryHandle;
  hudButtons: Pick<HudButtonsHandle, 'update'>;
  cruiseDrive: Pick<CruiseDrive, 'autopilotFlightLine'>;
  dvMap: DvMap;
  dvMapPanel: DvMapPanel;
  dvCorrection: DvCorrection;
  _cameraScene: THREE.Vector3;
  _cameraWorld: WorldVec;
  live: Pick<CruiseDriveLive, 'simBodies' | 'seedJd'>;
}

/** The live frame after the drive: camera, names, rebase, scene, render, HUD. Play splices `weapon-lock` in after `rebase`. */
export function createCruiseViewSystems(deps: CruiseViewSystemsDeps): FrameSystem[] {
  const {
    input, rig, bodyLabels, camera, simPos, syncShipTransform, progradeHud, worldFrame,
    atlasWorld, warp, syncTrajectoryCaption, simWorld, world, postFx, pilotPanel, pilot,
    trajectory, hudButtons, cruiseDrive, dvMap, dvMapPanel, dvCorrection,
    _cameraScene, _cameraWorld, live,
  } = deps;
  const whenLive = (c: FrameCtx): boolean => c.live;

  return [

{ name: 'camera', when: whenLive, update: (c) => {
  const realDt = c.realDt;
  // ----- Camera -----
  // Drained every frame whichever model is flying, so a fight cannot bank a
  // drag or a notch and spend it the moment cruise comes back.
  const chaseDrag = input.takeOrbit();
  const chaseNotches = input.takeWheel();
  if (!deps.fightCamera(realDt)) {
    // Cruise: the chase rig, solved off the ship's pose this frame
    // (src/cruise/cameraRig.ts).
    rig.update(realDt, chaseDrag, chaseNotches);
  }
} },

// Right after the camera, so a name sits on the body the frame is drawn from.
// Every frame rather than `whenLive`: a menu, the Atlas or a fight has to take
// the names down, not freeze them.
{ name: 'body-labels', profile: true, update: (c) => {
  bodyLabels.update(c.live && !deps.inFight() && !c.landOpen);
} },

{ name: 'rebase', when: whenLive, update: () => {
  // ----- Rebase -----
  // The ship or the camera has left the quantum, so the bubble's anchor moves
  // to the ship's own grid point. The camera and the ship ride inside the
  // bubble, so the only thing that changes is where the bubble sits in the
  // scene: every local metre in the fight, the camera's included, is untouched
  // and the frame the player is looking at is the same one.
  camera.getWorldPosition(_cameraScene);
  if (simPos.rebase(_cameraScene)) {
    syncShipTransform();
    deps.rebaseInFight();
  }
} },

{ name: 'scene-misc', when: whenLive, profile: true, update: (c) => {
  deps.sceneMisc.before(c);
  progradeHud.update();
  deps.sceneMisc.after(c);
} },

// Not `profile: true`: the mark inside times the composer draw alone, which is
// the span the perf panel's `render` row has always covered.
{ name: 'render', when: whenLive, update: () => {
  // ----- Render -----
  // The camera-relative pass puts a registered node at (world - camera), so the
  // frame's own root carries the camera's place in the scene and the node lands
  // at (world - anchor), which is the metres the sim is drawn in.
  camera.getWorldPosition(_cameraScene);
  worldFrame.root.position.copy(_cameraScene);
  worldFrame.applyCameraRelative(simPos.cameraWorld(_cameraScene, _cameraWorld));
  // One origin: the Atlas's world is placed from the same camera place and the
  // same heliocentric metres the ship's own rebase just wrote, so the bodies in
  // frame cannot drift against the ship between two rebases.
  const aw = atlasWorld();
  if (aw) {
    // What the Solar System view opens on: the body the ship is at.
    aw.setShipFocus(live.simBodies?.focus()?.row.id ?? null);
    // The world is drawn at the epoch the ship's own state is placed at, which
    // is the shared clock's: the body off the bow is the body the flight step
    // is falling toward, and it is also the body the Solar System view draws.
    aw.setWorldEpoch(Number.isFinite(live.seedJd) ? live.seedJd : null);
    // The body pulling hardest, from the approach reach in: the one body
    // cruise draws with its air, its rings and its own shape, in place of the
    // imagery globe every other row gets, eased in by `worldLook`.
    const near = worldLook(warp.envelope());
    aw.setWorldBody(near.id, near.look);
    aw.update(_cameraWorld, _cameraScene.x, _cameraScene.y, _cameraScene.z, camera);
  }
  syncTrajectoryCaption();
  // The Atlas may still be mounted behind a closed menu; take the drawing
  // buffer back, and the root, before drawing the sim through the composer.
  activeStage()?.release();
  simWorld.activate();
  const activeCam = world.activeCamera() ?? camera;
  profiler.mark('render'); postFx.render(activeCam); profiler.end('render');
} },

// Not `profile: true`: the `hud` mark is play's, in `hud.before`, after the
// inspected object's world position is read; with none, `profiler.end` is a no-op.
{ name: 'hud', when: whenLive, update: (c) => {
  deps.hud.before(c);
  pilotPanel.update({
    // A fight freezes the flight state - stepFlight is not called - so these
    // would read stale; null hides the panel the way the fight hides its own.
    pilot: deps.inFight() ? null : pilot.pilotReadout(),
    trajectory: trajectory.status(),
  });

  hudButtons.update();
  deps.hud.after(c);
  // A fight freezes the flight state, so a plan made off it would go stale the
  // way the pilot rows would; the map leaves the screen with the panel that
  // reads them and comes back when cruise does. Minimized or not is the
  // player's own choice and is not touched here.
  dvMap.setOpen(!deps.inFight());
  dvMap.update();
  dvMapPanel.setFlight(cruiseDrive.autopilotFlightLine());
  dvCorrection.update();
  dvMapPanel.setCorrection(dvCorrection.state());
  profiler.end('hud');
} },

  ];
}
